//  Copyright (c) 2019-2025 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import {AlertDialogComponent} from '../alert/alert.component';
import {catchError, switchMap} from 'rxjs/operators';
import {BehaviorSubject, EMPTY} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UserService} from '../../../services/user-service';
import {NotificationService} from '../../../services/notification-service';
import {IGroup, OrgType} from '../../../types/group';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-delete-organization-dialog',
    templateUrl: './delete-organization-dialog.component.html',
    styleUrls: ['./delete-organization-dialog.component.scss'],
    standalone: false
})
export class DeleteOrganizationDialogComponent implements OnInit {
    groups: IGroup[];
    selectedGroup: IGroup;
    processing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly dialog: MatDialog,
        private readonly notification: NotificationService,
        private readonly translate: TranslateService,
        private readonly userService: UserService,
        private readonly dialogRef: MatDialogRef<DeleteOrganizationDialogComponent>,
    ) {
    }

    ngOnInit(): void {
        this.userService.api_groups()
            .pipe(untilDestroyed(this))
            .subscribe(groups => this.groups = groups.filter(group => group.org_type == OrgType.CUSTOMER));
    }

    onClose() {
        this.dialogRef.close();
    }

    onSubmit() {
        this.processing$.next(true);
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            data: {
                title: this.translate.instant('DELETE_ORG_TITLE'),
                content: this.translate.instant('DELETE_ORG_CONTENT', {orgName: this.selectedGroup.name}),
                rejectBtnName: this.translate.instant('CANCEL'),
                acceptBtnName: this.translate.instant('CONFIRM'),
                showRejectBtn: true,
            },
        });
        dialogRef.afterClosed().pipe(
            switchMap(confirmation => {
                if (confirmation) {
                    return this.userService.delete_organization(this.selectedGroup.id);
                }
                return EMPTY;
            }),
            catchError(() => {
                this.notification.showError(this.translate.instant('ERROR_OCCURRED_UNKNOWN'));
                return EMPTY;
            })
        ).subscribe(
            () =>  this.notification.showSuccess(this.translate.instant('SUCCESS')),
            () => this.notification.showError(this.translate.instant('ERROR_OCCURRED_UNKNOWN')),
            () => this.dialogRef.close(),
        );
    }
}
